// /operators — operator (SaaS customer) landing + self-signup (COL-352).
//
// Implements Neel's "Operator Landing" design (Slack #tech-ops, Sep 2026):
// a dedicated marketing landing with a dark lounge hero and an inline signup /
// "book a demo" card. Petrol/turquoise palette + General Sans, per the CoLiviq
// design system.
//
// The flow per the rollout notes is unchanged: sign up → confirm email → land
// in /admin already logged in, with a data-naked but feature-rich console. The
// card is gated by VITE_OPERATORS_SIGNUP (off in prod until the COL-358
// isolation audit passes) — it shows a closed-rollout notice instead of the
// form when off.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  LandlordHero,
  LandlordBenefits,
  LandlordProduct,
  LandlordCalculator,
  LandlordPricing,
  LandlordFreeTier,
  LandlordOnboarding,
  LandlordSafety,
  LandlordPartners,
  LandlordConsult,
  LandlordVoices,
  LandlordFaq,
  LandlordClosing,
} from "@/components/operators/LandlordSections";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { isOperatorsSignupEnabled } from "@/lib/featureFlags";

type TFunc = (key: string, fallback?: string) => string;

// The feature carousel copy — verbatim from the design's slide deck,
// translated through t() (see operatorsLanding.carousel.slide1-6.*). The
// mock-browser screenshot area that used to sit under each slide was an
// empty placeholder and was dropped (Neel: "just remove the carousel" ->
// then "bring back the text, drop the screenshot placeholders").

// Display labels are translated; the submitted `value` stays the original
// English string in every language so signUp() metadata never depends on
// which language the visitor filled the form in (see the i18n migration).
const UNIT_OPTIONS: {
  value: string;
  labelKey: string;
  labelFallback: string;
}[] = [
  {
    value: "1–10 units",
    labelKey: "operatorsLanding.form.units.tiny",
    labelFallback: "1–10 rooms",
  },
  {
    value: "11–30 units",
    labelKey: "operatorsLanding.form.units.small",
    labelFallback: "11–30 rooms",
  },
  {
    value: "31–75 units",
    labelKey: "operatorsLanding.form.units.medium",
    labelFallback: "31–75 rooms",
  },
  {
    value: "76–150 units",
    labelKey: "operatorsLanding.form.units.large",
    labelFallback: "76–150 rooms",
  },
  {
    value: "150+ units",
    labelKey: "operatorsLanding.form.units.xlarge",
    labelFallback: "150+ rooms",
  },
];

/** General Sans webfont + interactive states, scoped to the landing page. */

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  height: 46,
  padding: "0 14px",
  border: "1px solid #E6EBEA",
  borderRadius: 12,
  background: "#F7F3EC",
  fontSize: 14,
  color: "#1A2B2A",
};
const labelStyle: React.CSSProperties = {
  display: "block",
  marginBottom: 7,
  fontSize: 13,
  fontWeight: 600,
  color: "#0F4C4A",
};
const req = (
  <span style={{ color: "#2FB3A9" }} aria-hidden>
    *
  </span>
);

/** The signup / "book a demo" card in the hero right column. */
function SignupCard() {
  const { signUp } = useAuth();
  const { t } = useLanguage();
  const enabled = isOperatorsSignupEnabled();
  const cardRef = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [company, setCompany] = useState("");
  const [units, setUnits] = useState("");
  const [market, setMarket] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reveal-on-mount (the design fades the card up once it enters the viewport).
  useEffect(() => {
    const t = window.setTimeout(() => setShown(true), 60);
    return () => window.clearTimeout(t);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(
        t(
          "operatorsLanding.form.errorPasswordTooShort",
          "Password must be at least 8 characters."
        )
      );
      return;
    }
    if (password !== confirm) {
      setError(
        t(
          "operatorsLanding.form.errorPasswordMismatch",
          "Passwords don't match."
        )
      );
      return;
    }
    setSubmitting(true);
    try {
      const { error: signUpError } = await signUp(
        email.trim(),
        password,
        fullName.trim(),
        {
          redirectPath: "/operators/confirmed",
          metadata: {
            account_type: "operator",
            company_name: company.trim(),
            // Lead context captured with the account (Neel's demo-call fields).
            units_managed: units || null,
            primary_market: market.trim() || null,
            phone: phone.trim() || null,
            note: note.trim() || null,
          },
        }
      );
      if (!signUpError) {
        setSentTo(email.trim());
      } else {
        setError(
          signUpError.message ??
            t(
              "operatorsLanding.form.errorGeneric",
              "Something went wrong. Please try again."
            )
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cardBase: React.CSSProperties = {
    scrollMarginTop: 88,
    background: "#fff",
    border: "1px solid rgba(255,255,255,0.5)",
    borderRadius: 24,
    padding: "34px 34px 30px",
    boxShadow:
      "0 30px 80px -40px rgba(6,32,31,0.55),0 4px 20px -10px rgba(6,32,31,0.35)",
  };

  if (sentTo) {
    return (
      <div
        id="apply"
        ref={cardRef}
        className={`ol-reveal ${shown ? "ol-in" : ""}`}
        style={cardBase}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2FB3A9"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <h2
            className="ol-display"
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.03em",
              color: "#0F4C4A",
            }}
          >
            {t("operatorsLanding.form.confirmEmailTitle", "Confirm your email")}
          </h2>
        </div>
        <p
          style={{
            margin: "12px 0 0",
            fontSize: 14,
            lineHeight: 1.55,
            color: "#5C6E6C",
          }}
        >
          {t(
            "operatorsLanding.form.confirmEmailPrefix",
            "We sent a confirmation link to"
          )}{" "}
          <strong>{sentTo}</strong>.{" "}
          {t(
            "operatorsLanding.form.confirmEmailSuffix",
            "Open it to activate your operator account — it takes you straight to your admin console."
          )}
        </p>
      </div>
    );
  }

  if (!enabled) {
    return (
      <div
        id="apply"
        ref={cardRef}
        className={`ol-reveal ${shown ? "ol-in" : ""}`}
        style={cardBase}
      >
        <h2
          className="ol-display"
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: "-0.03em",
            color: "#0F4C4A",
          }}
        >
          {t(
            "operatorsLanding.form.closedRolloutTitle",
            "Onboarding operators soon"
          )}
        </h2>
        <p
          style={{
            margin: "7px 0 0",
            fontSize: 13.5,
            lineHeight: 1.55,
            color: "#5C6E6C",
          }}
        >
          {t(
            "operatorsLanding.form.closedRolloutPrefix",
            "We're onboarding the first operators in a closed rollout. Write to"
          )}{" "}
          <a
            href="mailto:hello@coliviq.de"
            style={{ color: "#2FB3A9", fontWeight: 600 }}
          >
            hello@coliviq.de
          </a>{" "}
          {t(
            "operatorsLanding.form.closedRolloutSuffix",
            "and we'll set up a call."
          )}
        </p>
      </div>
    );
  }

  return (
    <div
      id="apply"
      ref={cardRef}
      className={`ol-reveal ${shown ? "ol-in" : ""}`}
      style={cardBase}
    >
      <h2
        className="ol-display"
        style={{
          margin: 0,
          fontSize: 22,
          fontWeight: 600,
          letterSpacing: "-0.03em",
          color: "#0F4C4A",
        }}
      >
        {t("operatorsLanding.cta.bookDemo", "Book a demo call")}
      </h2>
      <p
        style={{
          margin: "7px 0 26px",
          fontSize: 13.5,
          lineHeight: 1.5,
          color: "#5C6E6C",
        }}
      >
        {t(
          "operatorsLanding.form.subtitle",
          "Tell us about who you are — we'll set up a short call."
        )}
      </p>

      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 18 }}
      >
        <div>
          <label htmlFor="op-name" style={labelStyle}>
            {t("operatorsLanding.form.fields.fullName", "Full name")} {req}
          </label>
          <input
            id="op-name"
            className="ol-input"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoComplete="name"
            placeholder="Jane Weber"
            style={inputStyle}
          />
        </div>
        <div>
          <label htmlFor="op-email" style={labelStyle}>
            {t("operatorsLanding.form.fields.email", "Email")} {req}
          </label>
          <input
            id="op-email"
            className="ol-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder="jane@yourcoliving.de"
            style={inputStyle}
          />
        </div>
        <div>
          <label
            htmlFor="op-pw"
            style={{
              ...labelStyle,
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <span>
              {t("operatorsLanding.form.fields.password", "Password")} {req}
            </span>
            <span style={{ fontSize: 11.5, fontWeight: 500, color: "#8B9997" }}>
              {t("operatorsLanding.form.fields.passwordHint", "8+ characters")}
            </span>
          </label>
          <input
            id="op-pw"
            className="ol-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            placeholder="••••••••"
            style={inputStyle}
          />
        </div>
        <div>
          <label htmlFor="op-pw-confirm" style={labelStyle}>
            {t(
              "operatorsLanding.form.fields.confirmPassword",
              "Confirm password"
            )}{" "}
            {req}
          </label>
          <input
            id="op-pw-confirm"
            className="ol-input"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            placeholder="••••••••"
            style={inputStyle}
          />
        </div>
        <div>
          <label
            htmlFor="op-company"
            style={{
              ...labelStyle,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <span>
              {t(
                "operatorsLanding.form.fields.companyLabel",
                "Company / operator name"
              )}{" "}
              <span
                style={{ fontSize: 11.5, fontWeight: 500, color: "#8B9997" }}
              >
                {t(
                  "operatorsLanding.form.fields.companyHint",
                  "Leave blank if independent"
                )}
              </span>
            </span>
          </label>
          <input
            id="op-company"
            className="ol-input"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            autoComplete="organization"
            placeholder={t(
              "operatorsLanding.form.fields.optionalHint",
              "Optional"
            )}
            style={inputStyle}
          />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridTemplateRows: "auto auto",
            columnGap: 14,
            rowGap: 7,
          }}
        >
          <div style={{ display: "contents" }}>
            <label htmlFor="op-size" style={{ ...labelStyle, marginBottom: 0 }}>
              {t("operatorsLanding.form.fields.unitsLabel", "Units managed")}{" "}
              {req}
            </label>
            <label htmlFor="op-city" style={{ ...labelStyle, marginBottom: 0 }}>
              {t("operatorsLanding.form.fields.marketLabel", "Primary market")}{" "}
              {req}
            </label>
          </div>
          <select
            id="op-size"
            className="ol-input"
            required
            value={units}
            onChange={(e) => setUnits(e.target.value)}
            style={{ ...inputStyle, appearance: "none" }}
          >
            <option value="">
              {t("operatorsLanding.form.fields.unitsPlaceholder", "Select…")}
            </option>
            {UNIT_OPTIONS.map((u) => (
              <option key={u.value} value={u.value}>
                {t(u.labelKey, u.labelFallback)}
              </option>
            ))}
          </select>
          <input
            id="op-city"
            className="ol-input"
            required
            value={market}
            onChange={(e) => setMarket(e.target.value)}
            placeholder="München"
            style={inputStyle}
          />
        </div>
        <div>
          <label htmlFor="op-phone" style={labelStyle}>
            {t("operatorsLanding.form.fields.phoneLabel", "Phone")}
          </label>
          <input
            id="op-phone"
            className="ol-input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
            placeholder="+49 89 54193354"
            style={inputStyle}
          />
        </div>
        <div>
          <label
            htmlFor="op-note"
            style={{
              ...labelStyle,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <span>
              {t(
                "operatorsLanding.form.fields.noteLabel",
                "Anything we should know?"
              )}{" "}
              <span
                style={{ fontSize: 11.5, fontWeight: 500, color: "#8B9997" }}
              >
                {t("operatorsLanding.form.fields.optionalHint", "Optional")}
              </span>
            </span>
          </label>
          <textarea
            id="op-note"
            className="ol-input"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t(
              "operatorsLanding.form.fields.notePlaceholder",
              "What you run, what you're using today, or when you'd like to start."
            )}
            style={{
              ...inputStyle,
              height: "auto",
              padding: "12px 14px",
              lineHeight: 1.5,
              resize: "vertical",
            }}
          />
        </div>

        {error && (
          <p role="alert" style={{ margin: 0, fontSize: 13, color: "#C0714F" }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          className="ol-cta"
          disabled={submitting}
          style={{
            marginTop: 4,
            width: "100%",
            height: 50,
            border: "none",
            borderRadius: 999,
            background: "#0F4C4A",
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            cursor: submitting ? "default" : "pointer",
            boxShadow: "0 6px 28px hsl(178,67%,18%,0.4)",
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting
            ? t("operatorsLanding.form.submitting", "Creating your account…")
            : t("operatorsLanding.cta.bookDemo", "Book a demo call")}
        </button>

        <p
          style={{
            margin: "2px 0 0",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12.5,
            lineHeight: 1.5,
            color: "#8B9997",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2FB3A9"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0 }}
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {t(
            "operatorsLanding.form.disclaimer",
            "Non-binding request. Nothing charged, nothing signed."
          )}
        </p>
      </form>
    </div>
  );
}

/** The auto-advancing feature carousel — title + description only, no
 *  screenshot/browser-chrome card (Neel: drop the placeholder, keep the
 *  clean text carousel style: eyebrow, big heading, nav arrows, dots). */

const Operators = () => {
  const { language } = useLanguage();
  const de = language === "de";
  // The signup / "book a demo" form is a popup — hidden until a CTA is
  // clicked (Neel #12), not always-on in the hero. Kept from the previous
  // page: the draft has no signup flow of its own, and "Jetzt starten"
  // needs somewhere to land.
  const [formOpen, setFormOpen] = useState(false);

  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF" }}>
      <LandlordHero
        de={de}
        onPackages={() => scrollTo("pakete")}
        onContact={() => setFormOpen(true)}
      />
      <LandlordBenefits de={de} />
      <LandlordProduct de={de} />
      <LandlordCalculator de={de} />
      <LandlordPricing de={de} onStart={() => setFormOpen(true)} />
      {/* Straight after the price, which is where someone decides. The
          existing pop-up above signs up an OPERATOR (company, units managed);
          this is the individual landlord's first step. */}
      <LandlordFreeTier de={de} />
      <LandlordOnboarding de={de} />
      <LandlordSafety de={de} />
      <LandlordPartners de={de} />
      <LandlordConsult de={de} onDemo={() => setFormOpen(true)} />
      <LandlordVoices de={de} />
      <LandlordFaq de={de} />
      <LandlordClosing de={de} onDemo={() => setFormOpen(true)} />

      {formOpen && (
        <div
          onClick={() => setFormOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100,
            background: "rgba(6,32,31,0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            overflowY: "auto",
            padding: "56px 20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 452 }}
          >
            <SignupCard />
          </div>
        </div>
      )}
    </div>
  );
};

export default Operators;
